import { getCurrentOperatorName } from '../../utils/currentOperator';
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import styled from 'styled-components';
import BaseTicketScreen from './BaseTicketScreen';
import { FormRow, FormGroup, Label, Input, Select, FieldError } from './shared/TicketStyledComponents';
import { getTodayDate } from '../../utils/dateUtils';
import { calendarsFor } from '../../utils/businessDate';
import { settlementCalendars } from '../../utils/settlement';
import { formatAmountWithCommas, parseAmountShorthand } from '../../utils/amountUtils';
import { Building2 } from 'lucide-react';
import {
  BandEyebrow,
  BandHeader,
  BandSubline,
  BandTitle,
  BandTitleGroup,
  StructuredBand,
  StructuredHeroPanel,
  StructuredWorkspace,
  formatCurrencyValue,
} from './shared/StructuredCreditTicketLayout';

import { DateField } from '../shared/DateField';

/**
 * Portco Debt acquisition ticket — BUY-only, position-anchored.
 *
 * Originates a sponsor-finance / portfolio-company-anchored debt facility.
 * Lifecycle (DRAW, REPAY) originates from the position dashboard on the
 * portco detail screen, not from this ticket — see
 * {@code feedback_position_anchored_dispose}.
 *
 * Mirrors {@code RealEstateEquityTicketScreen}: wraps {@link BaseTicketScreen}
 * so the trade traverses DRAFT → PRECLEAR → APPROVED → SENT → EXECUTED →
 * POSTED with the standard lifecycle banner, asset-manager / portfolio-manager
 * pickers, and pre-clear button. The silo + SMF are persisted up-front via
 * {@code POST /api/portco/{id}/debt/prepare}; the ticket then POSTs the
 * trade payload through the standard {@code /api/trade-transactions} pipe with
 * {@code ticketType=PORTCO_DEBT}, {@code eventType=ACQUIRE}, and the
 * {@code sourceRef} returned by prepare so duplicate save-draft clicks are
 * idempotent against {@code uq_trade_tx_source_ref}.
 *
 * Synthetic CUSIP uses prefix {@code PD}. Asset class is stamped {@code PRIV_FI}
 * (Private Fixed Income) with sub-class {@code SPONSOR_FIN} (Sponsor Finance,
 * V290) so the blotter renders this as "Private Fixed Income / Sponsor Finance"
 * rather than orphan FI.
 */

interface PortfolioCompany {
  id: number;
  companyName: string;
  syntheticCusip?: string;
  sector?: string;
  fundId?: number;
  status?: string;
}

const MetricsPanel = styled.div`
  background: rgba(15, 23, 42, 0.7);
  border: 1px solid rgba(167, 139, 250, 0.28);
  border-radius: 14px;
  padding: 16px;
  margin-top: 18px;
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
  margin-top: 12px;
`;

const MetricCard = styled.div<{ $alert?: boolean }>`
  background: ${p => p.$alert ? 'rgba(245, 158, 11, 0.12)' : 'rgba(2, 6, 23, 0.35)'};
  padding: 13px;
  border-radius: 11px;
  border: 1px solid ${p => p.$alert ? 'rgba(245, 158, 11, 0.34)' : 'rgba(71, 85, 105, 0.38)'};
`;

const MetricLabel = styled.div`
  color: #64748b;
  font-size: 10px;
  font-weight: 850;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-bottom: 5px;
`;

const MetricValue = styled.div<{ $color?: string }>`
  font-size: 18px;
  line-height: 1.24;
  font-weight: 900;
  color: ${p => p.$color ?? '#f8fafc'};
`;

const MetricSub = styled.div`
  font-size: 11px;
  color: #94a3b8;
  margin-top: 4px;
`;

const MetricPanelTitle = styled.div`
  color: #f8fafc;
  font-size: 14px;
  font-weight: 900;
`;

const PortcoDebtTicketScreen: React.FC = () => {
  const [params] = useSearchParams();
  const initialPortcoId = params.get('portcoId') || '';

  // Portcos are fetched here (not in BaseTicketScreen's dimension data) — the
  // selector is portco-specific. The list is captured in a closure and surfaced
  // to the children render-prop below.
  const [portcos, setPortcos] = useState<PortfolioCompany[]>([]);
  // Why the trade date field is empty although a date was typed (a weekend or holiday), with the next business day.
  const [tradeDateProblem, setTradeDateProblem] = useState<{ problem: string; fix: { iso: string; label: string } | null } | null>(null);

  useEffect(() => {
    axios.get<PortfolioCompany[]>('/api/portco').then(
      (r) => setPortcos(r.data || []),
      () => setPortcos([]),
    );
  }, []);

  const initialFormData = {
    // Portco debt ticket is acquisition-only; DRAW / REPAY originate from the
    // position dashboard.
    transactionType: 'BUY' as const,
    assetManager: '',
    portfolioManager: '',
    assetClass: 'Private Fixed Income',
    assetSubClass: 'Sponsor Finance',
    security: '',
    identifier: '',
    portfolio: '',
    amount: '',
    tradeDate: getTodayDate(),
    // Portco debt settles on closing date — no T+N convention. Default settle
    // = trade so the position bi-temporal anchor matches every follow-on
    // lifecycle event (DRAW/REPAY/ACCRUAL).
    settleDate: getTodayDate(),
    submittedBy: getCurrentOperatorName(),
    currency: 'USD',
    executedBy: '',
    notes: '',
    // Portco-debt specific
    portcoId: initialPortcoId,
    instrumentType: 'TLB',
    seniority: 'FIRST_LIEN',
    lender: '',
    facilityName: '',
    sponsorHeld: 'true',
    principal: '',
    drawn: '',
    undrawn: '',
    rateType: 'FLOAT' as const,
    baseRate: 'SOFR',
    spreadBps: '',
    originationDate: getTodayDate(),
    maturity: '',
  };

  const validateForm = (formData: any, action: string): { [key: string]: string } => {
    const errors: { [key: string]: string } = {};

    if (!formData.portcoId) errors.portcoId = 'Portfolio company is required';
    if (!formData.portfolio?.trim()) errors.portfolio = 'Portfolio is required';
    if (!formData.instrumentType) errors.instrumentType = 'Instrument type is required';
    if (!formData.principal) errors.principal = 'Principal is required';
    if (formData.principal && parseAmountShorthand(formData.principal) <= 0) errors.principal = 'Must be > 0';
    if (!formData.tradeDate) errors.tradeDate = tradeDateProblem ? `${tradeDateProblem.problem}${tradeDateProblem.fix ? ` — use ${tradeDateProblem.fix.label}` : ''}` : 'Trade Date is required';
    if (!formData.settleDate) errors.settleDate = 'Settle Date is required';
    if (!formData.originationDate) errors.originationDate = 'Origination Date is required';

    // Drawn ≤ principal sanity check.
    if (formData.principal && formData.drawn) {
      const p = parseAmountShorthand(formData.principal);
      const d = parseAmountShorthand(formData.drawn);
      if (d > p) errors.drawn = 'Drawn cannot exceed principal';
    }

    // Floating-rate spread is required when rateType=FLOAT (otherwise the loan
    // would have no all-in rate at origination).
    if (formData.rateType === 'FLOAT' && !formData.spreadBps) {
      errors.spreadBps = 'Spread (bps) is required for floating rate';
    }

    if (action === 'execute') {
      if (!formData.executedBy?.trim()) errors.executedBy = 'Executed By is required for execution';
    }

    return errors;
  };

  const buildTransactionData = async (formData: any, dimensionData: any): Promise<any> => {
    const portfolio = dimensionData.portfolios.find(
      (p: any) => p.portfolioName === formData.portfolio.trim()
    );
    if (!portfolio) {
      throw new Error(`Portfolio "${formData.portfolio}" not found`);
    }

    const principalNum = parseAmountShorthand(formData.principal);
    const drawnNum = formData.drawn ? parseAmountShorthand(formData.drawn) : principalNum;
    const undrawnNum = formData.undrawn
      ? parseAmountShorthand(formData.undrawn)
      : Math.max(principalNum - drawnNum, 0);

    // Step 1 — silo + SMF (no trade). Returns securityId, sourceRef, debtId, cusip.
    const preparePayload: Record<string, any> = {
      portfolioId: portfolio.id,
      instrumentType: formData.instrumentType,
      principal: principalNum,
      drawn: drawnNum,
      undrawn: undrawnNum,
      currency: formData.currency || 'USD',
      rateType: formData.rateType,
      seniority: formData.seniority,
      lender: formData.lender || null,
      facilityName: formData.facilityName || null,
      sponsorHeld: formData.sponsorHeld === 'true',
      originationDate: formData.originationDate,
    };
    if (formData.maturity) preparePayload.maturity = formData.maturity;
    if (formData.rateType === 'FLOAT') {
      preparePayload.baseRate = formData.baseRate;
      if (formData.spreadBps) preparePayload.spreadBps = parseInt(formData.spreadBps, 10);
    }

    const prepareRes = await axios.post(
      `/api/portco/${formData.portcoId}/debt/prepare`,
      preparePayload,
    );
    const { securityId, sourceRef } = prepareRes.data;

    // Resolve dropdown names → dimension ids (matches RealEstateEquityTicketScreen).
    let assetManagerId: number | null = null;
    if (formData.assetManager && formData.assetManager.trim()) {
      const am = dimensionData.assetManagers.find(
        (a: any) => a.assetManagerName === formData.assetManager.trim()
      );
      if (am?.id) assetManagerId = am.id;
    }
    let portfolioManagerId: number | null = null;
    if (formData.portfolioManager && formData.portfolioManager.trim()) {
      const pm = dimensionData.portfolioManagers.find(
        (p: any) => p.portfolioManagerName === formData.portfolioManager.trim()
      );
      if (pm?.id) portfolioManagerId = pm.id;
    }

    // Step 2 — full trade payload. Quantity = drawn (the funded amount that
    // hits fact_position); price=100 by sponsor-finance convention; amount =
    // qty*price/100 = drawn dollars. The portco-debt posting hand-off (legacy
    // public-FI path until PortcoDebtPostingSpi is registered) uses securityId
    // to materialise the position row.
    return {
      transactionType: formData.transactionType,
      securityId,
      portfolioId: portfolio.id,
      assetManagerId,
      portfolioManagerId,
      quantity: drawnNum,
      price: 100,
      amount: drawnNum,
      tradeDate: formData.tradeDate,
      settleDate: formData.settleDate,
      notes: formData.notes || '',
      createdBy: formData.submittedBy,
      executedBy: formData.executedBy || null,
      status: 'DRAFT',
      ticketType: 'PORTCO_DEBT',
      eventType: 'ACQUIRE',
      sourceRef,
      currency: formData.currency || 'USD',
    };
  };

  const loadTransactionData = async (transaction: any) => {
    // Re-hydrate portco-debt-specific fields (lender, facility name, drawn,
    // undrawn, spread, maturity, etc.) from the silo. The trade itself only
    // carries the lifecycle scalars + securityId; the facility terms live on
    // fact_portco_debt and must be looked up via the SMF.
    const securityId = transaction.securityId
      ?? transaction.security?.id
      ?? null;

    let debt: any = null;
    let portco: any = null;
    if (securityId) {
      try {
        const r = await axios.get(`/api/portco/debt/by-security/${securityId}`);
        debt = r.data?.debt ?? null;
        portco = r.data?.portco ?? null;
      } catch {
        // Silo not found (e.g. very old trades pre-V288). Fall through to the
        // trade scalars and let the user see whatever the trade carries.
      }
    }

    const principal = debt?.principal != null
      ? Number(debt.principal).toString()
      : (transaction.amount != null ? transaction.amount.toString() : '');
    const drawn = debt?.drawn != null
      ? Number(debt.drawn).toString()
      : (transaction.quantity != null ? transaction.quantity.toString() : '');
    const undrawn = debt?.undrawn != null
      ? Number(debt.undrawn).toString()
      : '';

    return {
      transactionType: transaction.transactionType || 'BUY',
      assetManager: transaction.assetManagerName || transaction.assetManager || '',
      portfolioManager: transaction.portfolioManagerName || transaction.portfolioManager || '',
      assetClass: transaction.assetClassName || 'Private Fixed Income',
      assetSubClass: transaction.assetSubClassName || 'Sponsor Finance',
      security: transaction.securityName || (portco?.companyName
        ? `${portco.companyName} — ${debt?.instrumentType || 'Debt'}`
        : ''),
      identifier: debt?.syntheticCusip || transaction.cusip || transaction.identifier || '',
      portfolio: transaction.portfolioName || transaction.portfolio || '',
      amount: principal,
      tradeDate: transaction.tradeDate || getTodayDate(),
      settleDate: transaction.settleDate || transaction.tradeDate || getTodayDate(),
      submittedBy: transaction.createdBy || getCurrentOperatorName(),
      currency: debt?.currency || transaction.currency || 'USD',
      executedBy: transaction.executedBy || '',
      notes: transaction.notes || '',
      // Portco-debt facility terms from the silo.
      portcoId: debt?.portcoId != null ? String(debt.portcoId) : '',
      instrumentType: debt?.instrumentType || '',
      seniority: debt?.seniority || '',
      lender: debt?.lender || '',
      facilityName: debt?.facilityName || '',
      sponsorHeld: debt?.sponsorHeld === false ? 'false' : 'true',
      principal,
      drawn,
      undrawn,
      rateType: debt?.rateType || 'FLOAT',
      baseRate: debt?.baseRate || 'SOFR',
      spreadBps: debt?.spreadBps != null ? String(debt.spreadBps) : '',
      originationDate: debt?.originationDate || transaction.tradeDate || getTodayDate(),
      maturity: debt?.maturity || '',
    };
  };

  return (
    <BaseTicketScreen
      title="Portco Debt Trade Entry"
      assetClass="Private Fixed Income"
      assetSubClass="Sponsor Finance"
      initialFormData={initialFormData}
      validateForm={validateForm}
      buildTransactionData={buildTransactionData}
      loadTransactionData={loadTransactionData}
    >
      {({ formData, setFormData, errors, dimensionData, transactionStatus, isFieldDisabled, handleInputChange, handleInputBlur }) => {
        const selectedPortco = portcos.find((p) => String(p.id) === String(formData.portcoId));

        // Real-time computed metrics
        const principalNum = formData.principal ? parseAmountShorthand(formData.principal) : null;
        const drawnNum = formData.drawn ? parseAmountShorthand(formData.drawn) : null;
        const undrawnDerived = (principalNum != null && drawnNum != null)
          ? Math.max(principalNum - drawnNum, 0)
          : null;
        const utilizationPct = (principalNum && drawnNum && principalNum > 0)
          ? (drawnNum / principalNum) * 100
          : null;
        const allInRatePct = (formData.rateType === 'FLOAT' && formData.spreadBps)
          ? parseFloat(formData.spreadBps) / 100
          : null;
        const showMetrics = principalNum != null || allInRatePct != null;

        return (
          <StructuredWorkspace>
            <StructuredHeroPanel
              toneName="portco"
              icon={Building2}
              eyebrow="Private Equity"
              title="Portco Debt Ticket"
              description="Originate sponsor-finance debt against a portfolio company while preserving the facility terms needed for later draws, repayments, accruals, and lifecycle events."
              badges={['Sponsor finance', 'BUY-only acquisition', 'Facility anchored']}
              signals={[
                { label: 'Portco', value: selectedPortco?.companyName || 'Select company', accent: true },
                { label: 'Principal', value: principalNum ? formatCurrencyValue(principalNum, formData.currency || 'USD') : '-', accent: true },
                { label: 'Instrument', value: formData.instrumentType || '-' },
                { label: 'Rate', value: formData.rateType === 'FLOAT' && formData.spreadBps ? `${formData.baseRate || 'BASE'} + ${formData.spreadBps} bps` : formData.rateType || '-' },
              ]}
            />

            <StructuredBand>
              <BandHeader>
                <BandTitleGroup>
                  <BandEyebrow $tone="portco">Trade Setup</BandEyebrow>
                  <BandTitle>Dates, Ownership, and Execution</BandTitle>
                  <BandSubline>Set the acquisition dates and operating ownership for the sponsor-finance ticket.</BandSubline>
                </BandTitleGroup>
              </BandHeader>
            {/* ── Row 1: Transaction Type, Trade Date, Settle Date ── */}
            <FormRow>
              <FormGroup>
                <Label htmlFor="transactionType">Transaction Type*</Label>
                <Select
                  id="transactionType"
                  value={formData.transactionType || 'BUY'}
                  onChange={() => { /* read-only: lifecycle events come from dashboard */ }}
                  disabled
                >
                  <option value="BUY">Originate (Buy)</option>
                  <option value="REVALUATION">Lifecycle Event</option>
                </Select>
              </FormGroup>

              <FormGroup>
                <Label htmlFor="tradeDate">Trade Date*</Label>
                <DateField
                  id="tradeDate"
                  value={formData.tradeDate ?? ''}
                  onProblem={(problem, fix) => setTradeDateProblem(problem ? { problem, fix } : null)}
                  className={errors.tradeDate ? 'error' : ''}
                  disabled={isFieldDisabled('tradeDate')}
                  calendars={calendarsFor('PORTCO_DEBT', formData.currency)}
                  onChange={iso => { handleInputChange('tradeDate', iso); handleInputBlur('tradeDate', iso); }}
/>
                {errors.tradeDate && <FieldError>{errors.tradeDate}</FieldError>}
              </FormGroup>

              <FormGroup>
                <Label htmlFor="settleDate">Settle Date*</Label>
                <DateField
                  id="settleDate"
                  value={formData.settleDate ?? ''}
                  className={errors.settleDate ? 'error' : ''}
                  disabled={isFieldDisabled('settleDate')}
                  calendars={settlementCalendars({ ticketType: 'PORTCO_DEBT', currency: formData.currency })}
                  onChange={iso => handleInputChange('settleDate', iso)}
                />
                {errors.settleDate && <FieldError>{errors.settleDate}</FieldError>}
              </FormGroup>
            </FormRow>

            {/* ── Row 2: Asset Manager, Portfolio Manager, Executed By ── */}
            <FormRow>
              <FormGroup>
                <Label htmlFor="assetManager">Asset Manager*</Label>
                <Select
                  id="assetManager"
                  value={formData.assetManager}
                  onChange={(e) => handleInputChange('assetManager', e.target.value)}
                  className={errors.assetManager ? 'error' : ''}
                  disabled={isFieldDisabled('assetManager')}
                >
                  <option value="">Select Asset Manager</option>
                  {dimensionData.assetManagers.map((am: any) => (
                    <option key={am.id} value={am.assetManagerName}>{am.assetManagerName}</option>
                  ))}
                </Select>
                {errors.assetManager && <FieldError>{errors.assetManager}</FieldError>}
              </FormGroup>

              <FormGroup>
                <Label htmlFor="portfolioManager">Portfolio Manager*</Label>
                <Select
                  id="portfolioManager"
                  value={formData.portfolioManager}
                  onChange={(e) => handleInputChange('portfolioManager', e.target.value)}
                  onBlur={(e) => handleInputBlur('portfolioManager', e.target.value)}
                  className={errors.portfolioManager ? 'error' : ''}
                  disabled={isFieldDisabled('portfolioManager')}
                >
                  <option value="">Select Portfolio Manager</option>
                  {dimensionData.portfolioManagers.map((pm: any) => (
                    <option key={pm.id} value={pm.portfolioManagerName}>{pm.portfolioManagerName}</option>
                  ))}
                </Select>
                {errors.portfolioManager && <FieldError>{errors.portfolioManager}</FieldError>}
              </FormGroup>

              <FormGroup>
                <Label htmlFor="executedBy">Executed By{transactionStatus === 'SENT' ? '*' : ''}</Label>
                <Select
                  id="executedBy"
                  value={formData.executedBy}
                  onChange={(e) => handleInputChange('executedBy', e.target.value)}
                  onBlur={(e) => handleInputBlur('executedBy', e.target.value)}
                  disabled={isFieldDisabled('executedBy')}
                  className={errors.executedBy ? 'error' : ''}
                >
                  <option value="">Select</option>
                  {dimensionData.users.map((user: any) => (
                    <option key={user.id} value={user.fullName}>
                      {user.fullName}{user.role ? ` (${user.role})` : ''}
                    </option>
                  ))}
                </Select>
                {errors.executedBy && <FieldError>{errors.executedBy}</FieldError>}
              </FormGroup>
            </FormRow>
            </StructuredBand>

            {/* ── Counterparty & Booking ── */}
            <StructuredBand>
              <BandHeader>
                <BandTitleGroup>
                  <BandEyebrow $tone="portco">Counterparty and Booking</BandEyebrow>
                  <BandTitle>Portfolio Company and Booking Portfolio</BandTitle>
                  <BandSubline>Bind the facility to the selected portfolio company and book the funded exposure to the target portfolio.</BandSubline>
                </BandTitleGroup>
              </BandHeader>
            <FormRow>
              <FormGroup style={{ flex: 2 }}>
                <Label htmlFor="portcoId">Portfolio Company*</Label>
                <Select
                  id="portcoId"
                  value={formData.portcoId}
                  onChange={(e) => {
                    handleInputChange('portcoId', e.target.value);
                    const p = portcos.find((x) => String(x.id) === e.target.value);
                    if (p) {
                      handleInputChange('security', `${p.companyName} — ${formData.instrumentType || 'Debt'}`);
                    }
                  }}
                  className={errors.portcoId ? 'error' : ''}
                  disabled={isFieldDisabled('portcoId')}
                >
                  <option value="">— Select a portfolio company —</option>
                  {portcos.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.companyName}{p.syntheticCusip ? ` · ${p.syntheticCusip}` : ''}
                      {p.sector ? ` · ${p.sector}` : ''}
                    </option>
                  ))}
                </Select>
                {errors.portcoId && <FieldError>{errors.portcoId}</FieldError>}
                {selectedPortco?.fundId != null && (
                  <span style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
                    Held by fund #{selectedPortco.fundId}
                  </span>
                )}
              </FormGroup>

              <FormGroup>
                <Label htmlFor="portfolio">Booking Portfolio*</Label>
                <Select
                  id="portfolio"
                  value={formData.portfolio}
                  onChange={(e) => handleInputChange('portfolio', e.target.value)}
                  onBlur={(e) => handleInputBlur('portfolio', e.target.value)}
                  className={errors.portfolio ? 'error' : ''}
                  disabled={isFieldDisabled('portfolio')}
                >
                  <option value="">Select Portfolio</option>
                  {dimensionData.portfolios.map((p: any) => (
                    <option key={p.id} value={p.portfolioName}>{p.portfolioName}</option>
                  ))}
                </Select>
                {errors.portfolio && <FieldError>{errors.portfolio}</FieldError>}
              </FormGroup>
            </FormRow>
            </StructuredBand>

            {/* ── Facility Terms ── */}
            <StructuredBand>
              <BandHeader>
                <BandTitleGroup>
                  <BandEyebrow $tone="portco">Facility Terms</BandEyebrow>
                  <BandTitle>Instrument, Seniority, Lender, and Facility Name</BandTitle>
                  <BandSubline>Define the sponsor-finance instrument before sizing the funded and unfunded commitments.</BandSubline>
                </BandTitleGroup>
              </BandHeader>
            <FormRow>
              <FormGroup>
                <Label htmlFor="instrumentType">Instrument Type*</Label>
                <Select
                  id="instrumentType"
                  value={formData.instrumentType}
                  onChange={(e) => handleInputChange('instrumentType', e.target.value)}
                  className={errors.instrumentType ? 'error' : ''}
                  disabled={isFieldDisabled('instrumentType')}
                >
                  <option value="TLA">Term Loan A (TLA)</option>
                  <option value="TLB">Term Loan B (TLB)</option>
                  <option value="REVOLVER">Revolver</option>
                  <option value="UNITRANCHE">Unitranche</option>
                  <option value="MEZZ">Mezzanine</option>
                  <option value="HYK">High-Yield Notes</option>
                  <option value="PIK_NOTE">PIK Note</option>
                </Select>
                {errors.instrumentType && <FieldError>{errors.instrumentType}</FieldError>}
              </FormGroup>

              <FormGroup>
                <Label htmlFor="seniority">Seniority</Label>
                <Select
                  id="seniority"
                  value={formData.seniority}
                  onChange={(e) => handleInputChange('seniority', e.target.value)}
                  disabled={isFieldDisabled('seniority')}
                >
                  <option value="FIRST_LIEN">First Lien</option>
                  <option value="SECOND_LIEN">Second Lien</option>
                  <option value="UNSECURED">Unsecured</option>
                  <option value="SUBORDINATED">Subordinated</option>
                </Select>
              </FormGroup>

              <FormGroup>
                <Label htmlFor="sponsorHeld">Sponsor-Held</Label>
                <Select
                  id="sponsorHeld"
                  value={formData.sponsorHeld}
                  onChange={(e) => handleInputChange('sponsorHeld', e.target.value)}
                  disabled={isFieldDisabled('sponsorHeld')}
                >
                  <option value="true">Yes — sits in our PE / credit book</option>
                  <option value="false">No — third-party lender of record</option>
                </Select>
              </FormGroup>
            </FormRow>

            <FormRow>
              <FormGroup style={{ flex: 2 }}>
                <Label htmlFor="lender">Lender</Label>
                <Input
                  id="lender"
                  type="text"
                  value={formData.lender}
                  onChange={(e) => handleInputChange('lender', e.target.value)}
                  placeholder="e.g. Ares Capital"
                  disabled={isFieldDisabled('lender')}
                />
              </FormGroup>

              <FormGroup style={{ flex: 2 }}>
                <Label htmlFor="facilityName">Facility Name</Label>
                <Input
                  id="facilityName"
                  type="text"
                  value={formData.facilityName}
                  onChange={(e) => handleInputChange('facilityName', e.target.value)}
                  placeholder="e.g. 2026 Senior Secured TLB"
                  disabled={isFieldDisabled('facilityName')}
                />
              </FormGroup>
            </FormRow>
            </StructuredBand>

            {/* ── Capital Stack ── */}
            <StructuredBand>
              <BandHeader>
                <BandTitleGroup>
                  <BandEyebrow $tone="portco">Capital Stack</BandEyebrow>
                  <BandTitle>Principal, Drawn Amount, Undrawn Capacity, and Currency</BandTitle>
                  <BandSubline>Separate committed principal from funded exposure so utilization is explicit at close.</BandSubline>
                </BandTitleGroup>
              </BandHeader>
            <FormRow>
              <FormGroup>
                <Label htmlFor="principal">Principal ($)*</Label>
                <Input
                  id="principal"
                  type="text"
                  value={formData.principal}
                  onChange={(e) => handleInputChange('principal', e.target.value)}
                  onBlur={(e) => {
                    const parsed = parseAmountShorthand(e.target.value);
                    if (parsed > 0) {
                      const formatted = formatAmountWithCommas(parsed);
                      setFormData((prev: any) => ({
                        ...prev,
                        principal: formatted,
                        amount: formatted,
                      }));
                    }
                    handleInputBlur('principal', parsed.toString());
                  }}
                  placeholder="e.g. 250M, 250000000"
                  className={errors.principal ? 'error' : ''}
                  disabled={isFieldDisabled('principal')}
                />
                {errors.principal && <FieldError>{errors.principal}</FieldError>}
              </FormGroup>

              <FormGroup>
                <Label htmlFor="drawn">Drawn at Close ($)</Label>
                <Input
                  id="drawn"
                  type="text"
                  value={formData.drawn}
                  onChange={(e) => handleInputChange('drawn', e.target.value)}
                  onBlur={(e) => {
                    const parsed = parseAmountShorthand(e.target.value);
                    if (parsed > 0) {
                      setFormData((prev: any) => ({ ...prev, drawn: formatAmountWithCommas(parsed) }));
                    }
                  }}
                  placeholder="defaults to principal"
                  className={errors.drawn ? 'error' : ''}
                  disabled={isFieldDisabled('drawn')}
                />
                {errors.drawn && <FieldError>{errors.drawn}</FieldError>}
              </FormGroup>

              <FormGroup>
                <Label htmlFor="undrawn">Undrawn Capacity ($)</Label>
                <Input
                  id="undrawn"
                  type="text"
                  value={formData.undrawn}
                  onChange={(e) => handleInputChange('undrawn', e.target.value)}
                  onBlur={(e) => {
                    const parsed = parseAmountShorthand(e.target.value);
                    if (parsed >= 0) {
                      setFormData((prev: any) => ({ ...prev, undrawn: formatAmountWithCommas(parsed) }));
                    }
                  }}
                  placeholder="0 if fully drawn"
                  disabled={isFieldDisabled('undrawn')}
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="currency">Currency</Label>
                <Select
                  id="currency"
                  value={formData.currency}
                  onChange={(e) => handleInputChange('currency', e.target.value)}
                  disabled={isFieldDisabled('currency')}
                >
                  <option value="">Select Currency</option>
                  {dimensionData.currencies.map((c: any) => (
                    <option key={c.id} value={c.currencyCode}>
                      {c.currencyCode} - {c.currencyName}
                    </option>
                  ))}
                </Select>
              </FormGroup>
            </FormRow>
            </StructuredBand>

            {/* ── Rate & Tenor ── */}
            <StructuredBand>
              <BandHeader>
                <BandTitleGroup>
                  <BandEyebrow $tone="portco">Rate and Tenor</BandEyebrow>
                  <BandTitle>Pricing Basis, Spread, Origination Date, and Maturity</BandTitle>
                  <BandSubline>Capture floating-rate economics and tenor terms used by downstream facility lifecycle views.</BandSubline>
                </BandTitleGroup>
              </BandHeader>
            <FormRow>
              <FormGroup>
                <Label htmlFor="rateType">Rate Type</Label>
                <Select
                  id="rateType"
                  value={formData.rateType}
                  onChange={(e) => handleInputChange('rateType', e.target.value)}
                  disabled={isFieldDisabled('rateType')}
                >
                  <option value="FLOAT">Floating</option>
                  <option value="FIXED">Fixed</option>
                </Select>
              </FormGroup>

              {formData.rateType === 'FLOAT' && (
                <>
                  <FormGroup>
                    <Label htmlFor="baseRate">Base Rate</Label>
                    <Select
                      id="baseRate"
                      value={formData.baseRate}
                      onChange={(e) => handleInputChange('baseRate', e.target.value)}
                      disabled={isFieldDisabled('baseRate')}
                    >
                      <option value="SOFR">SOFR</option>
                      <option value="EURIBOR">EURIBOR</option>
                      <option value="SONIA">SONIA</option>
                      <option value="TONAR">TONAR</option>
                    </Select>
                  </FormGroup>
                  <FormGroup>
                    <Label htmlFor="spreadBps">Spread (bps)*</Label>
                    <Input
                      id="spreadBps"
                      type="number"
                      value={formData.spreadBps}
                      onChange={(e) => handleInputChange('spreadBps', e.target.value)}
                      placeholder="e.g. 525"
                      className={errors.spreadBps ? 'error' : ''}
                      disabled={isFieldDisabled('spreadBps')}
                    />
                    {errors.spreadBps && <FieldError>{errors.spreadBps}</FieldError>}
                  </FormGroup>
                </>
              )}
            </FormRow>

            <FormRow>
              <FormGroup>
                <Label htmlFor="originationDate">Origination Date*</Label>
                <DateField
                  id="originationDate"
                  value={formData.originationDate ?? ''}
                  className={errors.originationDate ? 'error' : ''}
                  disabled={isFieldDisabled('originationDate')}
                  onChange={iso => handleInputChange('originationDate', iso)}
                />
                {errors.originationDate && <FieldError>{errors.originationDate}</FieldError>}
              </FormGroup>

              <FormGroup>
                <Label htmlFor="maturity">Maturity</Label>
                <DateField
                  id="maturity"
                  value={formData.maturity ?? ''}
                  disabled={isFieldDisabled('maturity')}
                  onChange={iso => handleInputChange('maturity', iso)}
                />
              </FormGroup>
            </FormRow>

            {/* ── Calculated Metrics ── */}
            {showMetrics && (
              <MetricsPanel>
                <MetricPanelTitle>Calculated Metrics</MetricPanelTitle>
                <MetricsGrid>
                  {principalNum != null && (
                    <MetricCard>
                      <MetricLabel>Principal</MetricLabel>
                      <MetricValue $color="#0d6efd">
                        ${(principalNum / 1e6).toFixed(1)}M
                      </MetricValue>
                      <MetricSub>{formData.currency || 'USD'} face</MetricSub>
                    </MetricCard>
                  )}
                  {utilizationPct != null && (
                    <MetricCard $alert={utilizationPct === 100}>
                      <MetricLabel>Utilization</MetricLabel>
                      <MetricValue $color={utilizationPct === 100 ? '#d97706' : '#198754'}>
                        {utilizationPct.toFixed(0)}%
                      </MetricValue>
                      <MetricSub>Drawn / Principal</MetricSub>
                    </MetricCard>
                  )}
                  {undrawnDerived != null && (
                    <MetricCard>
                      <MetricLabel>Undrawn Capacity</MetricLabel>
                      <MetricValue>
                        ${(undrawnDerived / 1e6).toFixed(1)}M
                      </MetricValue>
                      <MetricSub>Available to draw</MetricSub>
                    </MetricCard>
                  )}
                  {allInRatePct != null && (
                    <MetricCard>
                      <MetricLabel>Spread</MetricLabel>
                      <MetricValue $color="#0d6efd">
                        {formData.baseRate || 'BASE'} + {allInRatePct.toFixed(2)}%
                      </MetricValue>
                      <MetricSub>{formData.spreadBps} bps over base</MetricSub>
                    </MetricCard>
                  )}
                </MetricsGrid>
              </MetricsPanel>
            )}
            </StructuredBand>
          </StructuredWorkspace>
        );
      }}
    </BaseTicketScreen>
  );
};

export default PortcoDebtTicketScreen;
